import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useAdminMe } from '../auth/AdminContext';
import { Table, Td, Th, Tr } from '../components/data';
import { Badge, Banner, Button, ErrorState, Input, Loading, PageHeader, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { shortDate, timeAgo } from '../lib/format';
import { addAdmin, fetchAdmins, qk, removeAdmin, resendAdminInvite, type AdminRow } from '../queries/admin';

/**
 * Who can see this panel. Adding someone is by email: they sign in with the
 * Google account or verified password account for that address and they are
 * in. An email tells them so, and the table says whether that email actually
 * went out, because "invited" and "received an invitation" are not the same
 * thing. Removing is immediate; their next request is refused.
 */

/** What the invitation email did, in one line, and whether it needs another go. */
function InviteStatus({ admin }: { admin: AdminRow }) {
  const { invite } = admin;
  if (admin.signedIn) {
    return (
      <span className="flex flex-col gap-0.5">
        <Badge tone="go">Signed in</Badge>
        {invite.count > 0 ? (
          <span className="text-caption text-chalk-faint">
            invited {invite.count === 1 ? 'once' : `${invite.count} times`}
          </span>
        ) : null}
      </span>
    );
  }
  if (invite.error) {
    return (
      <span className="flex flex-col gap-0.5">
        <Badge tone="danger">Email failed</Badge>
        <span className="max-w-[18rem] text-caption text-chalk-dim" title={invite.error}>
          {invite.error}
        </span>
        {invite.sentAt ? <span className="text-caption text-chalk-faint">last delivered {timeAgo(invite.sentAt)}</span> : null}
      </span>
    );
  }
  if (invite.sentAt) {
    return (
      <span className="flex flex-col gap-0.5">
        <Badge tone="info">Invited</Badge>
        <span className="text-caption text-chalk-faint">
          email sent {timeAgo(invite.sentAt)}
          {invite.count > 1 ? `, ${invite.count} in all` : ''}
        </span>
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <Badge tone="neutral">Not invited</Badge>
      <span className="text-caption text-chalk-faint">no email has gone out</span>
    </span>
  );
}

function ResendButton({ admin }: { admin: AdminRow }) {
  const queryClient = useQueryClient();
  const resend = useMutation({
    mutationFn: () => resendAdminInvite(admin.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.admins() }),
  });
  const result = resend.data?.invite;
  return (
    <span className="flex flex-col items-end gap-0.5">
      <Button size="sm" variant={admin.invite.error && !admin.signedIn ? 'secondary' : 'ghost'} busy={resend.isPending} onClick={() => resend.mutate()}>
        {admin.invite.count > 0 ? 'Resend invite' : 'Send invite'}
      </Button>
      {resend.isError ? <span className="text-caption text-danger">{errorMessage(resend.error)}</span> : null}
      {resend.isSuccess && result ? (
        <span className={cx('text-caption', result.error ? 'text-danger' : 'text-go')}>{result.error ? 'Still failing' : 'Sent'}</span>
      ) : null}
    </span>
  );
}

function RemoveButton({ admin }: { admin: AdminRow }) {
  const me = useAdminMe();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => removeAdmin(admin.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.admins() }),
  });

  if (admin.id === me.id) return <span className="text-caption text-chalk-faint">you</span>;

  if (!confirming) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
        Remove
      </Button>
    );
  }
  return (
    <span className="flex items-center justify-end gap-1.5">
      <Button size="sm" variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
        Remove {admin.email}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={remove.isPending}>
        Keep
      </Button>
      {remove.isError ? <span className="text-caption text-danger">{errorMessage(remove.error)}</span> : null}
    </span>
  );
}

function AddAdminForm() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => addAdmin({ email: email.trim(), name: name.trim() || null }),
    onSuccess: () => {
      setEmail('');
      setName('');
      return queryClient.invalidateQueries({ queryKey: qk.admins() });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate();
  };

  return (
    <form onSubmit={submit} className="card px-4 py-4">
      <h2 className="text-h4 text-chalk">Add an admin</h2>
      <p className="mt-1 text-caption text-chalk-dim">
        They sign in with the Google account for this address, or an email/password account that has verified it.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Input label="Email" type="email" autoComplete="off" value={email} onChange={(event) => setEmail(event.target.value)} required />
        <Input label="Name (optional)" autoComplete="off" value={name} onChange={(event) => setName(event.target.value)} />
        <Button type="submit" variant="primary" busy={add.isPending}>
          Add and send invite
        </Button>
      </div>
      {add.isError ? (
        <div className="mt-3">
          <Banner tone="danger">{errorMessage(add.error)}</Banner>
        </div>
      ) : null}
      {add.isSuccess ? (
        <div className="mt-3">
          {add.data.invite.error ? (
            <Banner tone="caution" title={`${add.data.email} is an admin, but the invitation email did not go out.`}>
              {add.data.invite.error} They can still sign in; use "Send invite" in the table to try the email again.
            </Banner>
          ) : (
            <Banner tone="go" title={`${add.data.email} is an admin.`}>
              An email with the sign-in link is on its way to them.
            </Banner>
          )}
        </div>
      ) : null}
    </form>
  );
}

export function AdminsPage() {
  const admins = useQuery({ queryKey: qk.admins(), queryFn: fetchAdmins });

  return (
    <>
      <PageHeader title="Admins" subtitle="Who can see this panel." />

      {admins.isPending ? (
        <Loading />
      ) : admins.isError ? (
        <ErrorState message={errorMessage(admins.error)} onRetry={() => void admins.refetch()} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Admin</Th>
              <Th>Status</Th>
              <Th>Added</Th>
              <Th align="right">Last seen</Th>
              <Th align="right">Invitation</Th>
              <Th align="right" />
            </tr>
          </thead>
          <tbody>
            {admins.data.items.map((admin) => (
              <Tr key={admin.id}>
                <Td>
                  <span className="block text-chalk">{admin.name?.trim() || admin.email}</span>
                  {admin.name ? <span className="block text-caption text-chalk-faint">{admin.email}</span> : null}
                </Td>
                <Td>
                  <InviteStatus admin={admin} />
                </Td>
                <Td className="text-chalk-dim">
                  {shortDate(admin.createdAt)}
                  {admin.addedBy ? <span className="text-chalk-faint"> by {admin.addedBy.name?.trim() || admin.addedBy.email}</span> : null}
                </Td>
                <Td align="right" className="whitespace-nowrap text-chalk-dim">
                  {admin.lastSeenAt ? timeAgo(admin.lastSeenAt) : ''}
                </Td>
                <Td align="right">
                  <ResendButton admin={admin} />
                </Td>
                <Td align="right">
                  <RemoveButton admin={admin} />
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="mt-6">
        <AddAdminForm />
      </div>
    </>
  );
}
